/* istanbul ignore file */
import * as Sentry from '@sentry/react';
import * as React from 'react';

import { AuthService, Guards } from 'frontend-toolkit';
import {
  BrowserRouter,
  Link,
  Redirect,
  Route,
  Switch,
  withRouter,
} from 'react-router-dom';

import { Query } from '@apollo/client/react/components';
import { Loader, PanelRow } from '@teamsnap/teamsnap-ui';
import { observer } from 'mobx-react';

import { reactiveFeedbackMessage } from '../core/apollo-cache';
import ApolloClient from '../core/apollo-client';
import ENVIRONMENT from '../core/environment';
import * as AcceptInvite from '../core/gql/mutations/accept-invite';
import * as AllOrganizations from '../core/gql/queries/all-organizations';
import * as QUERY_FEEDBACK_MESSAGE from '../core/gql/queries/feedback-message';
import AppStateStore from '../stores/app-state-store';
import FeedbackPopup from './common/feedback-popup';
import inject from './inject';
import Forbidden from './pages/forbidden';
import PageNotFound from './pages/page-not-found';

import { AuthWrapper } from 'auth/dist/components';
import { AuthContext } from 'auth/dist/contexts';
import {
  AUTH_URL,
  CLIENT_ID,
  COGSWORTH_URL,
  FUSION_APP_URL,
} from '../core/constants';
import { ROUTES } from '../core/routes';
import {
  currentBaseUrl,
  getParameterByName,
  redirectToOrg,
  redirectToSignup,
} from '../core/utils';
import Toolbar from '../hub/toolbar';

import { useQuery } from '@apollo/client';
import { ToastProvider, TooltipProvider } from '@teamsnap/snap-ui';
import { formatRoleForDisplay, isOneOf, Role } from 'auth';
import { User } from 'auth/dist/contexts/auth_context';
import { IdentifyProperties } from 'core/ampli';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import { GqlOrganization } from 'core/gql/fragments/organization';
import * as ShowOrganization from 'core/gql/queries/show-organizations';
import {
  hasPermissionForOrg,
  hasPermissionForProgram,
  Permission,
} from 'core/permissions';
import { FeatureFlagService } from 'frontend-toolkit';
import { useAmplitude } from 'hooks';
import { matchPath, RouteProps, useHistory } from 'react-router';
import OrgNav from '../hub/hub-nav';
import LaunchNotes from '../hub/notifications/launch-notes';
import ArchivedSeasonBanner from './common/archived-season-banner';
import DisabledOrgGuard from './common/disabled-org-guard';
import Footer from './common/footer';
import useChurnZero from './common/useChurnZero';
import MerchantAccountWarningModal from './merchant-accounts/MerchantAccountWarningModal';
import AddMemberPage from './pages/add-member';
import CalendarPage from './pages/calendar';
import DisabledOrg from './pages/disabled-org';
import DivisionsPage from './pages/divisions';
import ProgramEdit from './pages/edit-program';
import EmployeePage from './pages/employee';
import FinancialsPage from './pages/financials';
import HistoricalSeasonsPage from './pages/historical-seasons';
import InvoicesPage from './pages/invoices';
import LoadingPage from './pages/loading';
import MemberPage from './pages/member-view';
import MembersPage from './pages/members';
import MessagesPage from './pages/messages';
import BackgroundCheckNew from './pages/new-background-check';
import NewMessagePage from './pages/new-message';
import ProgramNew from './pages/new-program';
import OrderDetailsPage from './pages/order-details';
import OrgSettingsPage from './pages/organization-settings';
import ParticipantPage from './pages/participant-view';
import ParticipantsPage from './pages/participants';
import ProgramReportPage from './pages/program-report';
import ProgramsPage from './pages/programs';
import RegistrationPage from './pages/registration';
import RegistrationDetails from './pages/registration-details';
import SeasonFinancialsPage from './pages/season-financials';
import SeasonRegistrationPage from './pages/season-registration';
import SeasonsPage from './pages/seasons';
import SentMessagePage from './pages/sent-message';
import StaffPage from './pages/staff';
import {
  CoachesPackPage,
  SponsorshipsPage,
  TournamentsPage,
  WebsitesPage,
} from './pages/teamsnap-apps-ad-pages';

export interface Props {
  appStateStore?: AppStateStore;
  frameless?: boolean;
}

export interface UrlParams {
  orgId?: string;
  programId?: string;
  seasonId?: string;
  memberId?: string;
  emailId?: string;
  staffId?: string;
}

type GuardedRouteProps = { condition: boolean } & RouteProps;

function GuardedRoute({ component, condition, ...rest }: GuardedRouteProps) {
  const history = useHistory();

  if (!condition) {
    history.push({
      pathname: ROUTES.FORBIDDEN,
      state: { source: history.location },
    });
    return <></>;
  }
  const Component = component;
  return <Route {...rest} render={(props) => <Component {...props} />} />;
}

const App = inject('appStateStore')(
  observer(
    withRouter(({ history, location }) => {
      const context = React.useContext(AuthContext);
      const { amplitudeIdentify, amplitudeTrack } = useAmplitude();
      let token = getParameterByName(window.location.href, 'token');

      if (token && window.location.pathname === '/accept') {
        redirectToSignup(`${currentBaseUrl()}?token=${token}`);
        token = null;
      }

      const resultStatuses = {
        errorAlreadyTaken: 'ERROR_ALREADY_TAKEN',
        success: 'SUCCESS',
      };

      const inviteAccepted = 'accepted';

      const handleInviteAcceptResponse = (data) => {
        const { acceptInvite } = data;
        const { invitation, result } = acceptInvite;
        const { organizationId, status } = invitation;
        if (status === inviteAccepted && result === resultStatuses.success) {
          redirectToOrg(organizationId);
        } else if (result === resultStatuses.errorAlreadyTaken) {
          reactiveFeedbackMessage({
            message:
              'This invitation has already been accepted by another user. Please contact an administrator.',
            autoclose: true,
            isError: true,
          });
        }
        token = null;
      };

      if (token) {
        // Delay this to allow the dashboard to render and the query to start listening to refetches.
        ApolloClient.mutate({
          mutation: AcceptInvite.MUTATION,
          variables: { token } as AcceptInvite.Input,
          refetchQueries: () => [{ query: AllOrganizations.QUERY }],
        })
          .then(({ data }) => {
            handleInviteAcceptResponse(data);
          })
          .catch(({ graphQLErrors }) => {
            reactiveFeedbackMessage({
              message:
                'This token is invalid. Please contact an administrator.',
              autoclose: true,
              isError: true,
            });
          });
      }

      const match = matchPath<UrlParams>(history.location.pathname, {
        path: [...Object.keys(ROUTES).map((key) => ROUTES[key])],
        exact: true,
        strict: true,
      });

      const urlParams: UrlParams = {
        orgId: null,
        programId: null,
        seasonId: null,
        memberId: null,
        emailId: null,
        staffId: null,
      };

      if (match && match.params) {
        Object.keys(match.params).forEach((key) => {
          urlParams[key] = match.params[key];
        });
      }

      const hideToolbar = [ROUTES.FORBIDDEN, ROUTES.DISABLED_ORG].some((x) =>
        matchPath(window.location.pathname, x)
      );

      const hideToolbarOnMobile = [
        ROUTES.SPONSORSHIPS,
        ROUTES.TOURNAMENTS,
        ROUTES.WEBSITES,
        ROUTES.COACHES_PACK,
      ].some((x) => matchPath(window.location.pathname, x));

      const organizationId = +urlParams.orgId;
      const programId = +urlParams.programId;
      const seasonId = +urlParams.seasonId;

      if (context.user?.id) Sentry.setUser({ id: `${context.user.id}` });
      if (organizationId) Sentry.setTag('orgId', `${organizationId}`);

      const currentRole = React.useMemo(
        () =>
          context.user?.roles.find(
            (role) => +role.organizationId === +organizationId
          )?.type,
        [organizationId, programId, seasonId]
      );

      useChurnZero({ organizationId });

      React.useEffect(() => {
        const { uciUuid } = context.user || {};
        if (currentRole && uciUuid && organizationId) {
          amplitudeIdentify(
            uciUuid,
            context.user?.email,
            formatRoleForDisplay(
              currentRole
            ) as IdentifyProperties['selected_org_staff_role'],
            organizationId.toString()
          );
        }
      }, [currentRole, context.user, organizationId]);

      React.useEffect(() => {
        const queryParams = new URLSearchParams(location.search);

        if (queryParams.has('login_completed')) {
          queryParams.delete('login_completed');
          history.replace({
            search: queryParams.toString(),
          });
        }

        if (queryParams.has('create_account')) {
          queryParams.delete('create_account');
          history.replace({
            search: queryParams.toString(),
          });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const { pathname } = location;
      React.useEffect(() => {
        amplitudeTrack.screenViewed(pathname);
      }, [pathname]);

      const isOrgInvoicingEnabled = FeatureFlagService.isFeatureEnabledForOrg(
        FeatureFlagConfig[FeatureFlags.ORG_INVOICING],
        ENVIRONMENT,
        +urlParams?.orgId
      );

      return (
        <TooltipProvider>
          <div
            className="u-flex organization-app"
            style={{
              minHeight: '100%',
            }}
          >
            {organizationId > 0 ? (
              <DisabledOrgGuard organizationId={organizationId} />
            ) : null}
            <React.Suspense
              fallback={
                <PanelRow mods="u-textCenter">
                  <div className="Loader-indicator">
                    <span className="SpinAnimation" />
                    <div className="Loader-indicatorText">
                      Loading organizations...
                    </div>
                  </div>
                </PanelRow>
              }
            >
              <Switch>
                <Route path="/organizations/:orgId/sponsorships">
                  <OrgNav hideOnMobile />
                </Route>
                <Route path="/organizations/:orgId/tournaments">
                  <OrgNav hideOnMobile />
                </Route>
                <Route path="/organizations/:orgId/websites">
                  <OrgNav hideOnMobile />
                </Route>
                <Route path="/organizations/:orgId/programs/:programId/seasons/:seasonId/participants">
                  <OrgNav />
                </Route>
                <Route path="/organizations/:orgId/programs/:programId/seasons/:seasonId/registration">
                  <OrgNav />
                </Route>
                <Route path="/organizations/:orgId/programs/:programId/seasons/:seasonId/financials">
                  <OrgNav />
                </Route>
                <Route path="/organizations/:orgId">
                  <OrgNav />
                </Route>
                <Route path="/organizations">
                  <OrgNav />
                </Route>
                <Route path="/accept" exact>
                  <OrgNav />
                </Route>
                <Route path="/" exact>
                  <OrgNav />
                </Route>
              </Switch>
              <main
                className={
                  'sui-w-full u-flex u-flexJustifyCenter u-posRelative sui-overflow-x-hidden'
                }
              >
                <div className="hub-full-width">
                  {!hideToolbar && (
                    <>
                      <LaunchNotes userId={context.user?.id} />
                      <Toolbar
                        Link={Link}
                        orgId={urlParams.orgId}
                        hideOnMobile={hideToolbarOnMobile}
                        showNavigationSearch={isOneOf(
                          `${organizationId}`,
                          context.user,
                          Role.Admin,
                          Role.Owner,
                          Role.OrgAdmin,
                          Role.ProgramAdmin,
                          Role.ProgramCoordinator
                        )}
                      />
                    </>
                  )}
                  <Route
                    path={
                      '/organizations/:orgId/programs/:programId/seasons/:seasonId'
                    }
                    render={({
                      match: {
                        params: { seasonId },
                      },
                    }) => <ArchivedSeasonBanner seasonId={+seasonId} />}
                  />
                  <Switch>
                    <Route
                      exact
                      path="/employee"
                      render={() => <Redirect to="/employee/organizations" />}
                    />
                    <GuardedRoute
                      path="/employee/organizations"
                      condition={AuthService.checkGuard(
                        Guards.AdminOrGhostInspector,
                        context.user as User
                      )}
                      component={EmployeePage}
                    />
                    <GuardedRoute
                      exact
                      path={ROUTES.PROGRAM_INDEX}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.ProgramsView,
                        urlParams?.orgId
                      )}
                      component={ProgramsPage}
                    />
                    <GuardedRoute
                      exact
                      path={ROUTES.HISTORICAL_SEASONS}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.ProgramsView,
                        urlParams?.orgId
                      )}
                      component={HistoricalSeasonsPage}
                    />
                    <GuardedRoute
                      exact
                      path={ROUTES.PROGRAM_SEASONS}
                      condition={hasPermissionForProgram(
                        context.user,
                        Permission.ProgramsView,
                        urlParams?.orgId,
                        urlParams?.programId
                      )}
                      component={SeasonsPage}
                    />
                    <GuardedRoute
                      exact
                      path={ROUTES.PROGRAM_NEW}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.ProgramCreate,
                        urlParams?.orgId
                      )}
                      component={ProgramNew}
                    />
                    <GuardedRoute
                      exact
                      path={ROUTES.PROGRAM_EDIT}
                      condition={hasPermissionForProgram(
                        context.user,
                        Permission.ProgramsView,
                        urlParams?.orgId,
                        urlParams?.programId
                      )}
                      component={ProgramEdit}
                    />
                    <GuardedRoute
                      exact
                      path={ROUTES.PROGRAM_REPORT}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.ProgramCreate,
                        urlParams?.orgId
                      )}
                      component={ProgramReportPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.MembersView,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.MEMBER_INDEX}
                      component={MembersPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.MembersUpdate,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.MEMBER_NEW}
                      component={AddMemberPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.MembersView,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.MEMBER}
                      component={MemberPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.MembersView,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.MEMBER_BACKGROUND_CHECK_NEW}
                      component={BackgroundCheckNew}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.OrgUpdate,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.MERCHANT_ACCOUNTS}
                      component={OrgSettingsPage as any}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.StaffView,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.STAFF_INDEX}
                      component={StaffPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.StaffUpdate,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.STAFF_BACKGROUND_CHECK_NEW}
                      component={BackgroundCheckNew}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.MessagesView,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.MESSAGE_NEW}
                      component={NewMessagePage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.MessagesView,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.MESSAGE_SENT}
                      component={SentMessagePage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.MessagesView,
                        urlParams?.orgId
                      )}
                      exact
                      path={ROUTES.MESSAGE_INDEX}
                      component={MessagesPage}
                    />
                    <GuardedRoute
                      exact
                      path={ROUTES.SETTINGS_INDEX}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.OrgUpdate,
                        urlParams?.orgId
                      )}
                      component={OrgSettingsPage as any}
                    />
                    <GuardedRoute
                      path={ROUTES.REGISTRATION_DETAILS}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.OrgView,
                        urlParams?.orgId
                      )}
                      component={RegistrationDetails}
                    />
                    <GuardedRoute
                      path={ROUTES.REGISTRATION_INDEX}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.OrgView,
                        urlParams?.orgId
                      )}
                      component={RegistrationPage}
                    />
                    <GuardedRoute
                      condition={
                        hasPermissionForOrg(
                          context.user,
                          Permission.OrgView,
                          urlParams?.orgId
                        ) && isOrgInvoicingEnabled
                      }
                      exact
                      path={ROUTES.INVOICES_INDEX}
                      component={InvoicesPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForProgram(
                        context.user,
                        Permission.SeasonRegistrationView,
                        urlParams?.orgId,
                        urlParams?.programId
                      )}
                      path={ROUTES.SEASON_REGISTRATIONS_INDEX}
                      component={SeasonRegistrationPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForProgram(
                        context.user,
                        Permission.MembersView,
                        urlParams?.orgId,
                        urlParams?.programId
                      )}
                      exact
                      path={ROUTES.PARTICIPANT}
                      component={ParticipantPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForProgram(
                        context.user,
                        Permission.MembersView,
                        urlParams?.orgId,
                        urlParams?.programId
                      )}
                      exact
                      path={ROUTES.PARTICIPANTS_INDEX}
                      component={ParticipantsPage}
                    />
                    <GuardedRoute
                      condition={hasPermissionForProgram(
                        context.user,
                        Permission.MembersView,
                        urlParams?.orgId,
                        urlParams?.programId
                      )}
                      exact
                      path={ROUTES.SEASON_FINANCIALS}
                      component={SeasonFinancialsPage}
                    />
                    <GuardedRoute
                      path={ROUTES.ORDER_DETAILS}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.OrgFinancialView,
                        urlParams?.orgId
                      )}
                      component={OrderDetailsPage}
                    />
                    <GuardedRoute
                      path={ROUTES.FINANCIALS}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.OrgFinancialView,
                        urlParams?.orgId
                      )}
                      component={FinancialsPage}
                    />
                    <GuardedRoute
                      path={ROUTES.CALENDAR}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.OrgCalendarView,
                        urlParams?.orgId
                      )}
                      component={CalendarPage}
                    />
                    <GuardedRoute
                      exact
                      path={ROUTES.DIVISION_INDEX}
                      condition={hasPermissionForOrg(
                        context.user,
                        Permission.CoordinatedDivisionsView,
                        urlParams?.orgId
                      )}
                      component={DivisionsPage}
                    />
                    <Route
                      path={ROUTES.TOURNAMENTS}
                      component={TournamentsPage}
                    />
                    <Route path={ROUTES.WEBSITES} component={WebsitesPage} />
                    <Route
                      path={ROUTES.COACHES_PACK}
                      component={CoachesPackPage}
                    />
                    <Route
                      path={ROUTES.SPONSORSHIPS}
                      component={SponsorshipsPage}
                    />
                    <Route
                      exact
                      path={ROUTES.FORBIDDEN}
                      component={Forbidden}
                    />
                    <Route
                      exact
                      path={ROUTES.DISABLED_ORG}
                      component={DisabledOrg}
                    />
                    <Route exact path={ROUTES.HOME} component={LoadingPage} />
                    <Route
                      path="/organizations/:orgId"
                      render={({ match }) => {
                        const { orgId } = match.params;
                        if (
                          hasPermissionForOrg(
                            context.user,
                            Permission.ProgramsView,
                            orgId
                          )
                        ) {
                          return (
                            <Redirect to={`/organizations/${orgId}/programs`} />
                          );
                        } else if (
                          hasPermissionForOrg(
                            context.user,
                            Permission.CoordinatedDivisionsView,
                            orgId
                          )
                        ) {
                          return (
                            <Redirect
                              to={`/organizations/${orgId}/divisions`}
                            />
                          );
                        } else {
                          return <Redirect to={ROUTES.FORBIDDEN} />;
                        }
                      }}
                    />
                    <Route component={PageNotFound} />
                  </Switch>
                </div>
                <Footer />
              </main>
              <Query query={QUERY_FEEDBACK_MESSAGE.QUERY}>
                {({ loading, data }) => {
                  if (loading) {
                    return null;
                  }

                  if (
                    !data ||
                    !data.feedbackMessage ||
                    !data.feedbackMessage.message
                  ) {
                    return null;
                  }

                  const clear = () => {
                    reactiveFeedbackMessage({
                      message: null,
                      autoclose: null,
                      isError: null,
                    });
                  };

                  return (
                    <FeedbackPopup
                      onClose={clear}
                      autoClose={data.feedbackMessage.autoclose}
                      type={
                        data.feedbackMessage.isError ? 'negative' : 'positive'
                      }
                    >
                      {data.feedbackMessage.message}
                    </FeedbackPopup>
                  );
                }}
              </Query>

              {
                <MerchantAccountWarningModal
                  orgId={+organizationId}
                  showManageAccountsButton={isOneOf(
                    `${organizationId}`,
                    context.user,
                    Role.Admin,
                    Role.Owner,
                    Role.OrgAdmin
                  )}
                />
              }
            </React.Suspense>
          </div>
          <ToastProvider />
        </TooltipProvider>
      );
    })
  )
);

const WrappedApp = () => {
  // NOTE: The current pattern of our URL is /organizations/:orgId/...
  const orgId = window.location.pathname.split('/')[2];
  const { data: currentOrganization, loading: isLoadingCurrentOrganization } =
    useQuery<{ showOrganization: GqlOrganization }>(
      ShowOrganization.BASIC_ORG_QUERY,
      {
        variables: { organizationId: Number(orgId) },
      }
    );

  // NOTE: Redirect fusion orgs to fusion app
  React.useEffect(() => {
    if (currentOrganization?.showOrganization.isOrgV2) {
      window.location.href = `${FUSION_APP_URL}/organizations/${currentOrganization?.showOrganization.id}`;
    }
  }, [currentOrganization]);

  if (
    isLoadingCurrentOrganization ||
    currentOrganization?.showOrganization.isOrgV2
  ) {
    return (
      <div className="sui-grid sui-w-full sui-h-full sui-items-center sui-justify-center sui-fixed">
        <Loader
          type="spin"
          size={{ width: 40, height: 40 }}
          message="Loading..."
        />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AuthWrapper
        clientId={CLIENT_ID}
        cogsworthUrl={COGSWORTH_URL}
        authEndpoint={AUTH_URL}
        loadingIndicator={
          <div className="sui-grid sui-w-full sui-h-full sui-items-center sui-justify-center sui-fixed">
            <Loader
              type="spin"
              size={{ width: 40, height: 40 }}
              message="Loading..."
            />
          </div>
        }
      >
        <App />
      </AuthWrapper>
    </BrowserRouter>
  );
};

export default WrappedApp;
